/**
 * React Query hooks for WT-4 (fieldiq-api#1253): the Well Testing rate card,
 * a rent-in's billing rate, and field tickets — the Pipeline & Facility hooks
 * (usePfTickets.ts) carried over, since this vertical's API mirrors PF-4.
 *
 * Same rules as useWellTesting.ts: every key starts
 * ['wellTesting'] and every write invalidates that one prefix (a line
 * moves the ticket, the register's total and the job's Tickets tab); every
 * read spreads LOAD_FAILURE_QUERY_OPTIONS so a refusal reads as a refusal,
 * never as an empty list.
 *
 * A write that can return a signed ticket to draft resolves to
 * `{ ticket, reverted }` so the screen can say so — `reverted_to_draft` is
 * never dropped on the way.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrapArray } from '../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { invalidateWt, unwrapWt, unwrapWtList } from './useWellTesting';
import type {
  WtRate,
  WtRateWrite,
  WtRentInBilling,
  WtTicket,
  WtTicketEdit,
  WtTicketHeaderWrite,
  WtTicketLineCreate,
  WtTicketLinePatch,
  WtTicketRecipient,
  WtTicketSendResult,
} from '../types/wt-tickets';

type Params = Record<string, string | undefined>;
const clean = (p?: Params) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  Object.entries(p).forEach(([k, v]) => {
    if (v) out[k] = v;
  });
  return Object.keys(out).length ? out : undefined;
};

export const wtTicketKeys = {
  rates: (p?: Params) => ['wellTesting', 'rates', clean(p) ?? {}] as const,
  rate: (id: string) => ['wellTesting', 'rate', id] as const,
  rentInBilling: (id: string) => ['wellTesting', 'rentInBilling', id] as const,
  tickets: (p?: Params) => ['wellTesting', 'tickets', clean(p) ?? {}] as const,
  ticket: (id: string) => ['wellTesting', 'ticket', id] as const,
  recipient: (id: string) => ['wellTesting', 'ticketRecipient', id] as const,
  equipmentTypes: () => ['wellTesting', 'equipmentTypes'] as const,
};

function useWtWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateWt(qc) });
}

/**
 * A delete: drop the deleted record's own query and do NOT wait on the refetch.
 * Awaiting invalidateWt here refetched the gone record (404, with retries) for
 * ~4s before the screen's onSuccess could navigate away — measured on PF's PFT-0003.
 */
function useWtDelete(fn: (id: string) => Promise<unknown>, key: (id: string) => readonly unknown[]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: key(id), exact: true });
      void invalidateWt(qc);
    },
  });
}

/** A ticket write's echo: the ticket as it now is, and whether it went back to draft. */
export interface WtTicketEditResult {
  ticket: WtTicket | null;
  reverted: boolean;
}
const editResult = (body: WtTicketEdit | unknown): WtTicketEditResult => ({
  ticket: unwrapWt<WtTicket>(body).data,
  reverted: !!(body && typeof body === 'object' && (body as { reverted_to_draft?: unknown }).reverted_to_draft === true),
});

// ─── Rate card ───────────────────────────────────────────────────────────────

export interface WtRateFilters extends Params {
  /** A customer's uuid, or 'default'. */
  customer_id?: string;
  kind?: string;
  on?: string;
}

export function useWtRates(filters?: WtRateFilters, enabled = true) {
  return useQuery({
    queryKey: wtTicketKeys.rates(filters),
    queryFn: async () => unwrapWtList<WtRate>((await api.wellTesting.rates.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWtRate(id: string | undefined) {
  return useQuery({
    queryKey: wtTicketKeys.rate(id ?? ''),
    queryFn: async () => unwrapWt<WtRate>((await api.wellTesting.rates.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWtRate = () =>
  useWtWrite(async (body: WtRateWrite) => unwrapWt<WtRate>((await api.wellTesting.rates.create(body)).data));
export const useUpdateWtRate = () =>
  useWtWrite(async ({ id, body }: { id: string; body: WtRateWrite }) =>
    unwrapWt<WtRate>((await api.wellTesting.rates.update(id, body)).data));
export const useDeleteWtRate = () => useWtDelete(async (id: string) => (await api.wellTesting.rates.remove(id)).data, wtTicketKeys.rate);

/** The tenant's equipment types, for an owned-equipment rate. */
export function useWtEquipmentTypes(enabled = true) {
  return useQuery({
    queryKey: wtTicketKeys.equipmentTypes(),
    queryFn: async () =>
      unwrapArray<{ id: string; name?: string | null }>((await api.equipmentTypes.list()).data)
        .map((t) => ({ id: t.id, name: t.name || '—' }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

// ─── A rent-in's billing rate ────────────────────────────────────────────────

export function useWtRentInBillingRates(rentInIds: string[], enabled = true) {
  return useQueries({
    queries: rentInIds.map((id) => ({
      queryKey: wtTicketKeys.rentInBilling(id),
      queryFn: async () => unwrapWt<WtRentInBilling>((await api.wellTesting.rentInBilling.get(id)).data),
      enabled,
      ...LOAD_FAILURE_QUERY_OPTIONS,
    })),
  });
}

export const useSetWtRentInBillingRate = () =>
  useWtWrite(async ({ rentInId, rateId }: { rentInId: string; rateId: string | null }) =>
    unwrapWt<WtRentInBilling>((await api.wellTesting.rentInBilling.set(rentInId, rateId)).data));

// ─── Tickets ─────────────────────────────────────────────────────────────────

export interface WtTicketFilters extends Params {
  job_id?: string;
  customer_id?: string;
  status?: string;
}

export function useWtTickets(filters?: WtTicketFilters, enabled = true) {
  return useQuery({
    queryKey: wtTicketKeys.tickets(filters),
    queryFn: async () => unwrapWtList<WtTicket>((await api.wellTesting.tickets.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWtTicket(id: string | undefined) {
  return useQuery({
    queryKey: wtTicketKeys.ticket(id ?? ''),
    queryFn: async () => unwrapWt<WtTicket>((await api.wellTesting.tickets.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Who a send would address — the office's write role only (the API gates it as a write). */
export function useWtTicketRecipient(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: wtTicketKeys.recipient(id ?? ''),
    queryFn: async () => unwrapWt<WtTicketRecipient>((await api.wellTesting.tickets.recipient(id as string)).data),
    enabled: !!id && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWtTicket = () =>
  useWtWrite(async ({ jobId, workDate }: { jobId: string; workDate?: string }) =>
    unwrapWt<WtTicket>((await api.wellTesting.tickets.create(jobId, workDate ? { work_date: workDate } : {})).data));

/** Refresh answers `refreshed.reverted` rather than `reverted_to_draft`; both reach the screen as `reverted`. */
export const useRefreshWtTicket = () =>
  useWtWrite(async (id: string): Promise<WtTicketEditResult> => {
    const body = (await api.wellTesting.tickets.refresh(id)).data as { refreshed?: { reverted?: unknown } } | undefined;
    return { ticket: unwrapWt<WtTicket>(body).data, reverted: body?.refreshed?.reverted === true };
  });

export const useUpdateWtTicket = () =>
  useWtWrite(async ({ id, body }: { id: string; body: WtTicketHeaderWrite }) =>
    editResult((await api.wellTesting.tickets.update(id, body)).data));

export const useDeleteWtTicket = () => useWtDelete(async (id: string) => (await api.wellTesting.tickets.remove(id)).data, wtTicketKeys.ticket);

export const useAddWtTicketLine = () =>
  useWtWrite(async ({ id, body }: { id: string; body: WtTicketLineCreate }) =>
    editResult((await api.wellTesting.tickets.addLine(id, body)).data));

export const useUpdateWtTicketLine = () =>
  useWtWrite(async ({ id, lineId, body }: { id: string; lineId: string; body: WtTicketLinePatch }) =>
    editResult((await api.wellTesting.tickets.updateLine(id, lineId, body)).data));

export const useRemoveWtTicketLine = () =>
  useWtWrite(async ({ id, lineId }: { id: string; lineId: string }) =>
    editResult((await api.wellTesting.tickets.removeLine(id, lineId)).data));

export const useOfficeApproveWtTicket = () =>
  useWtWrite(async (id: string) => unwrapWt<WtTicket>((await api.wellTesting.tickets.officeApprove(id)).data));

export const useSendWtTicket = () =>
  useWtWrite(async ({ id, email, cc }: { id: string; email?: string; cc?: string[] }): Promise<WtTicketSendResult> => {
    const body: { email?: string; cc?: string[] } = {};
    if (email) body.email = email;
    if (cc && cc.length) body.cc = cc;
    return (await api.wellTesting.tickets.send(id, body)).data;
  });

export const useRecordWtTicketApproval = () =>
  useWtWrite(async ({ id, form }: { id: string; form: FormData }) =>
    unwrapWt<WtTicket>((await api.wellTesting.tickets.recordApproval(id, form)).data));

/** Void keeps the ticket on file and stops it billing; the reason is required (400 `reason_required`). */
export const useVoidWtTicket = () =>
  useWtWrite(async ({ id, reason }: { id: string; reason: string }) =>
    unwrapWt<WtTicket>((await api.wellTesting.tickets.void(id, reason)).data));
